import { useEffect, useRef, useState } from 'react'
import { useRealtimeKitMeeting, useRealtimeKitSelector } from '@cloudflare/realtimekit-react'
import { createMediaQueue, selfTile, toTile, type MeetingClient, type TileParticipant } from './rtk'
import { rememberDevice, type DeviceKind } from './devices'

/**
 * The SDK's media state as one plain object (board fqw7: shared by the
 * meeting stage, the ongoing-meeting card and the session provider, so it
 * lives here rather than in the route). Hooks in this file may only run
 * under a `RealtimeKitProvider` — the provider renders them in a bridge.
 */

export interface MediaState {
  self: TileParticipant | null
  remotes: TileParticipant[]
  activeSpeakerId: string | null
  /** This tab's own microphone, for the transcriber. */
  audioTrack: MediaStreamTrack | null
  micOn: boolean
  cameraOn: boolean
  sharing: boolean
  /** In-call device switching (board m5gc #11). */
  audioDevices: MediaDeviceInfo[]
  videoDevices: MediaDeviceInfo[]
  audioDeviceId: string | null
  videoDeviceId: string | null
  setAudioDevice: (deviceId: string) => Promise<boolean>
  setVideoDevice: (deviceId: string) => Promise<boolean>
  /** Each returns false when the device refused — the caller says so (REVIEW-2 #5). */
  toggleMic: () => Promise<boolean>
  toggleCamera: () => Promise<boolean>
  toggleShare: () => Promise<boolean>
}

export const NO_MEDIA: MediaState = {
  self: null,
  remotes: [],
  activeSpeakerId: null,
  audioTrack: null,
  micOn: false,
  cameraOn: false,
  sharing: false,
  audioDevices: [],
  videoDevices: [],
  audioDeviceId: null,
  videoDeviceId: null,
  setAudioDevice: async () => true,
  setVideoDevice: async () => true,
  toggleMic: async () => true,
  toggleCamera: async () => true,
  toggleShare: async () => true,
}

/**
 * Everything the dock and the bar need, read through
 * `useRealtimeKitSelector` so the SDK's own update stream drives re-renders.
 */
export function useRealtimeKitMediaState(): MediaState {
  const { meeting } = useRealtimeKitMeeting()
  const enqueue = useRef(createMediaQueue()).current
  const micOn = useRealtimeKitSelector((m) => m.self.audioEnabled)
  const cameraOn = useRealtimeKitSelector((m) => m.self.videoEnabled)
  const sharing = useRealtimeKitSelector((m) => m.self.screenShareEnabled)
  const activeSpeakerId = useRealtimeKitSelector((m) => m.participants.lastActiveSpeaker)
  const joinedMap = useRealtimeKitSelector((m) => m.participants.joined)
  // A selector, not a plain read: a device switch replaces the track while
  // `audioEnabled` stays true, and the transcriber must follow the new
  // track (board m5gc #11) — the mute/unmute path already restarts it.
  const audioTrack = useRealtimeKitSelector((m) => m.self.audioTrack ?? null)
  const devices = useSelfDevices(meeting)

  return {
    self: selfTile(meeting.self),
    remotes: [...joinedMap.values()].map(toTile),
    activeSpeakerId: activeSpeakerId || null,
    audioTrack,
    micOn,
    cameraOn,
    sharing,
    audioDevices: devices.audio,
    videoDevices: devices.video,
    audioDeviceId: devices.audioId,
    videoDeviceId: devices.videoId,
    setAudioDevice: (deviceId) => enqueue(() => applyDevice(meeting, 'audio', deviceId)),
    setVideoDevice: (deviceId) => enqueue(() => applyDevice(meeting, 'video', deviceId)),
    toggleMic: () =>
      enqueue(async () => {
        if (micOn) await meeting.self.disableAudio()
        else await meeting.self.enableAudio()
      }),
    toggleCamera: () =>
      enqueue(async () => {
        if (cameraOn) await meeting.self.disableVideo()
        else await meeting.self.enableVideo()
      }),
    toggleShare: () =>
      enqueue(async () => {
        if (sharing) await meeting.self.disableScreenShare()
        else await meeting.self.enableScreenShare()
      }),
  }
}

/**
 * Put one of the browser's devices on RealtimeKit and remember it for the
 * session. Resolving through the SDK's own list matters: `setDevice` wants
 * its MediaDeviceInfo, and an id the SDK cannot find is logged rather than
 * silently skipped — silence is how the wrong microphone went unnoticed.
 */
export async function applyDevice(
  meeting: MeetingClient,
  kind: DeviceKind,
  deviceId: string | undefined
): Promise<void> {
  if (!deviceId) return
  const device = await meeting.self.getDeviceById(deviceId, kind)
  if (!device) {
    console.warn(
      `[meet] ${kind} device ${deviceId} is not known to RealtimeKit — using its default`
    )
    return
  }
  await meeting.self.setDevice(device)
  rememberDevice(kind, deviceId)
}

/**
 * The SDK's view of the input devices and which ones are current, refreshed
 * on its own device events (a switch, a plug/unplug, a permission grant).
 */
function useSelfDevices(meeting: MeetingClient) {
  const [state, setState] = useState<{
    audio: MediaDeviceInfo[]
    video: MediaDeviceInfo[]
    audioId: string | null
    videoId: string | null
  }>({ audio: [], video: [], audioId: null, videoId: null })

  useEffect(() => {
    let cancelled = false
    const refresh = async () => {
      try {
        const [audio, video] = await Promise.all([
          meeting.self.getAudioDevices(),
          meeting.self.getVideoDevices(),
        ])
        const current = meeting.self.getCurrentDevices()
        if (cancelled) return
        setState({
          audio: audio.filter((device) => device.deviceId),
          video: video.filter((device) => device.deviceId),
          audioId: current?.audio?.deviceId ?? null,
          videoId: current?.video?.deviceId ?? null,
        })
      } catch (err) {
        console.warn('[meet] could not list devices', err)
      }
    }
    void refresh()
    const events = ['deviceUpdate', 'deviceListUpdate', 'audioUpdate', 'videoUpdate'] as const
    for (const event of events) meeting.self.on(event, refresh)
    return () => {
      cancelled = true
      for (const event of events) meeting.self.removeListener(event, refresh)
    }
  }, [meeting])

  return state
}
